import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import { Loader2, X } from "lucide-react";
import { ROUTES } from "@/routes";
import { formatEpoch, parseTracks } from "../types";

const client = generateClient<Schema>();

interface Partner {
  id: string;
  businessName: string | null;
  identityVerificationCompletedAt: number | null;
}

interface Props {
  businessId: string;
  onClose: () => void;
}

export function IdentityCollisionSheet({ businessId, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const lcResult = await client.models.MerchantLifecycle.get({
          businessId,
        });
        const lifecycle = lcResult.data;
        if (!lifecycle) {
          if (!cancelled) {
            setPartners([]);
            setLoading(false);
          }
          return;
        }
        const tracks = parseTracks(lifecycle.tracks);
        const identityTrack = tracks?.identity;
        const metadata = identityTrack?.metadata as
          { collidingBusinessIds?: unknown } | undefined;
        const collidingIds: string[] = Array.isArray(
          metadata?.collidingBusinessIds,
        )
          ? (metadata.collidingBusinessIds as unknown[]).filter(
              (x): x is string => typeof x === "string",
            )
          : [];

        const partnerResults = await Promise.all(
          collidingIds.map(async (id) => {
            const r = await client.models.BusinessProfile.get({ id });
            const bp = r.data;
            return {
              id,
              businessName: bp?.businessName ?? null,
              identityVerificationCompletedAt:
                bp?.identityVerificationCompletedAt ?? null,
            } as Partner;
          }),
        );

        if (!cancelled) {
          setPartners(partnerResults);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Failed to load collision partners",
          );
          setLoading(false);
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/10"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="border-border bg-card fixed top-0 right-0 bottom-0 z-50 flex w-[480px] max-w-[95vw] flex-col border-l shadow-xl">
        <div className="border-border flex items-start justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-foreground text-lg font-semibold">
              Identity collision
            </h2>
            <p className="text-muted-foreground mt-1 text-xs">
              {loading
                ? "Loading partner merchants…"
                : `Shares identity with ${partners.length} merchant${partners.length === 1 ? "" : "s"}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-subtle-foreground hover:bg-muted hover:text-muted-foreground rounded p-1"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          {!loading && !error && partners.length === 0 && (
            <div className="text-muted-foreground text-sm">
              No collision partners found.
            </div>
          )}
          {!loading && partners.length > 0 && (
            <ul className="space-y-2">
              {partners.map((p) => (
                <li
                  key={p.id}
                  className="border-border bg-muted rounded-lg border px-3 py-2"
                >
                  <Link
                    to={ROUTES.MERCHANT_DETAIL.replace(":businessId", p.id)}
                    className="block text-sm font-medium text-blue-600 hover:underline"
                  >
                    {p.businessName ?? p.id}
                  </Link>
                  {p.businessName && (
                    <div className="text-subtle-foreground mt-0.5 font-mono text-xs">
                      {p.id}
                    </div>
                  )}
                  {p.identityVerificationCompletedAt != null && (
                    <div className="text-muted-foreground mt-1 text-xs">
                      Verified {formatEpoch(p.identityVerificationCompletedAt)}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
